import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { prefersReducedMotion } from '@/components/layout/shell/entrance';
import type { NavTone } from '@/components/layout/shell/types';
import type { ObserverGesture } from '@/components/observer/observerContext';
import { cn } from '@/components/ui/cn';
import { CORE_R, LEAF_D, MARK_HALF, PETALS, PETAL_D } from './geometry';
import { FAN_FULL, FAN_OFF, isWhole, type PetalState, type Petals } from './petals';
import {
  ALARM_GAP_MS,
  ALARM_MS,
  BLOOM_QUICK,
  BLOOM_SLOW,
  EASE_SETTLE,
  EASE_SETTLE_STRONG,
  RING_MS,
  SNAP,
  STEP_REVEAL,
} from './schedule';

/*
  KanameMark — фирменный знак: шесть твёрдых лепестков вокруг кораллового ядра
  (docs/brand-concept.md, R41). Лепестки не текут и не меняют форму — только поворот и прозрачность,
  поэтому движение лёгкое: браузер двигает готовые слои, ничего не перерисовывая.

  Раскрытие веера: первый лепесток проявляется на 6 часах, каждый следующий выходит из-за
  предыдущего по часовой стрелке, каскадом, с замедлением в конце. Складывание — обратно.
  Темп: быстрый (знак в меню) или неспешный (сцена входа).

  «Глубина» (сцена входа): лепестки медленно темнеют и становятся окнами — тёмная грань с тонкой
  рамкой. За окнами одно общее пространство — листопад: листья идут общим потоком из правого верхнего
  угла вниз влево, за всеми лепестками и за ядром; видно их только сквозь окна.

  Ядро живёт отдельно: дома — коралловая сфера (может дышать и тянуться к курсору), вышло — тонкое
  кольцо на его месте. Веер в меню живёт сам (дыхание или поворот), а от порыва ветра — пасхалки
  с быстрыми нажатиями — раскручивается вокруг ядра, как вертушка.

  Язык состояний (R42, помощник R36) — всё необязательно, без этих свойств знак ровно прежний:
  - petals — поза каждого лепестка (petals.ts): сложен, раскрыт, пропуск с пунктирным призраком,
    погашен. Пока веер несимметричен, он не поворачивается, а порыв доводит его целыми оборотами;
  - reveal — как выходят новые лепестки: по расписанию раскрытия, шагом загрузки или досадкой;
  - tone — тон ядра по шкале срочности (круг тона поверх коралла), у кольца — цвет тона;
  - rhythm — ритм ядра: вдвое чаще, «затаил дыхание», двойной удар ожидания;
  - cue — разовый отклик ядра: тревога (два удара и кольцо), кольцо или жест из языка Наблюдателя.
*/

interface KanameMarkProps {
  /** Веер раскрыт (знак) или сложен за ядром. */
  open?: boolean;
  /** Ядро дома, вышло (тонкое кольцо) или скрыто. */
  core?: 'home' | 'away' | 'none';
  /** Ядро дышит — знак «живой» (на главной, на сцене входа). */
  breathing?: boolean;
  /** Лепестки сплошные (знак) или окна «глубины» — переход между ними плавный. */
  variant?: 'solid' | 'window';
  /** За окнами идёт листопад (только вместе с variant="window"). */
  leaves?: boolean;
  /** Темп раскрытия: быстрый (меню) или неспешный (сцена входа). */
  tempo?: 'quick' | 'slow';
  /** Сдвиг ядра к курсору, в единицах знака. */
  coreShift?: { x: number; y: number };
  /**
   * Веер живёт сам по себе (знак в меню): дышит в такт ядру — слегка прикрывается к первому лепестку
   * и раскрывается обратно, — время от времени поворачивается на один лепесток, или и то и другое
   * (both — так в меню, решение Максима). Без этого неподвижен.
   */
  life?: MarkLife;
  /** Порыв ветра (пасхалка, useGust): каждое новое значение раскручивает веер вокруг ядра. */
  gust?: number;
  /** Подпись для читалок; без неё знак декоративный. */
  label?: string;
  className?: string;
  /** Поза каждого лепестка (petals.ts); перекрывает open: open — это FAN_FULL, open={false} — FAN_OFF. */
  petals?: Petals;
  /** Как выходят новые лепестки: по расписанию раскрытия (bloom), шагом загрузки (step), досадкой (snap). */
  reveal?: MarkReveal;
  /** Тон ядра — состояние системы по шкале срочности; brand — коралл, как всегда. */
  tone?: MarkTone;
  /** Ритм ядра: спокойный, вдвое чаще (сбой), «затаил дыхание» (нет связи), двойной удар (ждёт ответа). */
  rhythm?: MarkRhythm;
  /** Разовый отклик ядра: играет только при смене at после монтирования. */
  cue?: MarkCue | null;
}

export type MarkLife = 'breath' | 'turn' | 'both';
export type MarkReveal = 'bloom' | 'step' | 'snap';
export type MarkTone = NavTone;
export type MarkRhythm = 'calm' | 'quick' | 'still' | 'await';
/**
 * Тревога, кольцо вокруг ядра или жест из языка Наблюдателя (motion.css, [data-gesture]).
 * nod — поворот шара: на голом ядре почти не виден, поэтому в галерее его нет; свой кивок ядра — с телом
 * помощника (этап D).
 */
export type MarkGesture = 'alarm' | 'burst' | ObserverGesture;
export interface MarkCue {
  gesture: MarkGesture;
  at: number;
}

const FOLD_STEP_MS = { quick: 70, slow: 110 } as const;

/** Дыхание ядра и веера идёт по общим часам страницы — в такт, сколько бы раз ни включалось. */
const BREATH_ANIMATIONS = new Set(['core-breathe', 'petal-breathe', 'core-await']);

/** Погашенная пластина («нет доступа»). */
const DIM_OPACITY = 0.3;

/** Пластина на месте (раскрыта или погашена) — её поза одна и та же. */
const placed = (s: PetalState) => s === 'on' || s === 'dim';

/*
  Тревога ядра: два удара — сильный и слабее, затем покой. Играет на своей обёртке через WAAPI, а не
  перемонтажом: общие часы дыхания внутри не сбиваются.
*/
const ALARM_BEATS: Keyframe[] = [
  { transform: 'scale(1)', offset: 0 },
  { transform: 'scale(1.14)', offset: 0.15 },
  { transform: 'scale(1)', offset: 0.45 },
  { transform: 'scale(1.1)', offset: 0.6 },
  { transform: 'scale(1)', offset: 1 },
];

/*
  Кольцо отклика: кадры observer-burst (motion.css), но тоже через WAAPI, а не перемонтажом: схлопнутый
  повтор тревоги не обрывает идущее кольцо, а вернувшееся ядро не проигрывает прошлый отклик заново.
*/
const RING_BURST: Keyframe[] = [
  { scale: 0.5, opacity: 0.9 },
  { scale: 1.9, opacity: 0 },
];
const EASE_RING = 'cubic-bezier(0.2, 0, 0, 1)';

/*
  Порыв ветра: веер резко набирает обороты (первые 9 % времени), делает около трёх оборотов и
  затихает — скорость падает как (1 − t)^2.5, как у вертушки. Останавливается на угле, кратном 60°:
  знак шестикратно симметричен, поэтому замирает ровно в исходном виде. Новый порыв, пока веер
  крутится, подхватывает его на ходу — сразу с полной скоростью. Кривая посчитана один раз и
  разложена в опорные точки, гуще в начале, где разгон.
*/
const GUST_MS = 2800;
const GUST_TURNS = 3;

function gustCurve(rise: number): [offset: number, progress: number][] {
  const steps = 2000;
  const speed = (t: number) =>
    t < rise ? Math.sin((Math.PI * t) / (2 * rise)) ** 2 : (1 - (t - rise) / (1 - rise)) ** 2.5;
  const sum = [0];
  for (let i = 1; i <= steps; i++) sum.push(sum[i - 1] + speed(i / steps));
  return Array.from({ length: 49 }, (_, i) => {
    const t = (i / 48) ** 1.6;
    return [t, sum[Math.round(t * steps)] / sum[steps]];
  });
}
const GUST_FROM_REST = gustCurve(0.09);
const GUST_ON_THE_GO = gustCurve(0);

/** Повторяемый «случай»: картинка листопада одна и та же при каждом показе. */
function seeded(seed: number) {
  let t = seed;
  return () => {
    t = (t * 9301 + 49297) % 233280;
    return t / 233280;
  };
}

/*
  Листопад за окнами: три слоя глубины. Дальние — мелкие, тусклые и медленные; ближние — крупные,
  яркие и быстрые. За лепестками одно общее пространство: листья идут общим потоком из правого
  верхнего угла вниз влево через весь знак, покачиваясь поперёк полёта и кувыркаясь. Сквозь окна
  видно, как поток проходит за всеми лепестками и за ядром; старт и финиш — за краем знака, так что
  на виду лист не тает, а уходит за край. Поток ровный: каждое окно получает примерно поровну
  (проверено моделью знака с окнами).
*/
const LAYERS = [
  { count: 20, scale: 1.1, dur: [20, 24.5], opacity: 0.5 },
  { count: 18, scale: 1.7, dur: [15.3, 18.4], opacity: 0.85 },
  { count: 10, scale: 2.4, dur: [10.7, 13.8], opacity: 1 },
] as const;
const TONES = ['fill-core', 'fill-core-light', 'fill-peach'] as const;
/** Куда дует: 125° — вниз влево (0° — вправо, 90° — вниз); у каждого листа разброс до ±8°. */
const FLOW_DEG = 125;
const FLOW_JITTER_DEG = 8;
/** Ширина потока в каждую сторону от оси через центр и откуда-докуда летит лист, в единицах знака. */
const FLOW_BAND = 540;
const FLOW_HALF = 640;

const LEAVES = (() => {
  const rnd = seeded(7);
  const f = (FLOW_DEG * Math.PI) / 180;
  const dx = Math.cos(f);
  const dy = Math.sin(f);
  return LAYERS.flatMap((layer, li) =>
    Array.from({ length: layer.count }, (_, i) => {
      const dur = layer.dur[0] + rnd() * (layer.dur[1] - layer.dur[0]);
      // Поперёк потока — поровну, внутри доли случайно: поток ровный, без проплешин.
      const p = FLOW_BAND * ((2 * (i + rnd())) / layer.count - 1);
      const g = f + ((rnd() * 2 - 1) * FLOW_JITTER_DEG * Math.PI) / 180;
      const sx = -FLOW_HALF * dx - p * dy;
      const sy = -FLOW_HALF * dy + p * dx;
      const ex = sx + 2 * FLOW_HALF * Math.cos(g);
      const ey = sy + 2 * FLOW_HALF * Math.sin(g);
      return {
        key: `${li}-${i}`,
        sx: Math.round(sx),
        sy: Math.round(sy),
        ex: Math.round(ex),
        ey: Math.round(ey),
        // Ось покачивания — поперёк полёта: лист трепещет, а не дёргается вперёд-назад.
        axis: Math.round((g * 180) / Math.PI + 90),
        sway: 14 + rnd() * 26,
        tilt: 25 + rnd() * 40,
        swayDur: 1.8 + rnd() * 1.6,
        scale: layer.scale * (0.85 + rnd() * 0.3),
        opacity: layer.opacity,
        dur,
        delay: -rnd() * dur,
        tone: TONES[Math.floor(rnd() * TONES.length)],
      };
    }),
  );
})();

export function KanameMark({
  open = true,
  core = 'home',
  breathing = false,
  variant = 'solid',
  leaves = false,
  tempo = 'quick',
  coreShift,
  life,
  gust = 0,
  label,
  className,
  petals,
  reveal = 'bloom',
  tone = 'brand',
  rhythm = 'calm',
  cue,
}: KanameMarkProps) {
  const id = useId();
  const gradient = `kn-core-${id}`;
  const toneGradient = `kn-tone-${id}`;
  const glow = `kn-glow-${id}`;
  const clip = `kn-windows-${id}`;
  const schedule = tempo === 'slow' ? BLOOM_SLOW : BLOOM_QUICK;
  const windowed = variant === 'window';
  const svgRef = useRef<SVGSVGElement>(null);
  const breathes = life === 'breath' || life === 'both';
  // Уменьшенное движение (R19): лепестки встают без задержек, как есть.
  const reduced = prefersReducedMotion();

  // Веер: поза каждого лепестка. Прошлая поза хранится, чтобы знать, кто выходит и откуда.
  const fan = petals ?? (open ? FAN_FULL : FAN_OFF);
  const fanKey = fan.join(' ');
  const [wave, setWave] = useState({ key: fanKey, from: fan, to: fan });
  if (wave.key !== fanKey) setWave({ key: fanKey, from: wave.to, to: fan });
  const was = wave.key === fanKey ? wave.from : wave.to;
  // Несимметричный веер не поворачивается: дыра или погашенная пластина поехали бы по кругу.
  const whole = isWhole(fan);
  const turns = (life === 'turn' || life === 'both') && whole;
  // Кто выходит сейчас (из сложенной позы) — по порядку k, для лесенки шага и досадки.
  const arriving: number[] = PETALS.filter((k) => placed(fan[k]) && !placed(was[k]));

  // Тон: круг тона гаснет в последнем тоне, поэтому его цвет держится до следующего тона.
  const [paint, setPaint] = useState(tone);
  if (tone !== 'brand' && tone !== paint) setPaint(tone);

  // Ритм ядра: вдвое чаще, двойной удар или спокойное дыхание; «затаил дыхание» — пауза (mark-still).
  const beat =
    rhythm === 'quick'
      ? 'animate-core-breathe-quick'
      : rhythm === 'await'
        ? 'animate-core-await'
        : breathing
          ? 'animate-core-breathe'
          : undefined;

  // Ядро и веер дышат по общим часам знака: начало каждого дыхания — общее, сначала это начало страницы.
  // Пока знак «затаил дыхание», все дыхания стоят в одной позе — и прежние, и появившиеся за паузу
  // (сменился ритм ядра). После паузы часы знака сдвигаются на её длину: веер продолжает с той же позы,
  // а ядро — в такт ему.
  const stillNow = rhythm === 'still';
  const clock = useRef(0);
  const held = useRef<number | null>(null);
  const ghosts = petals !== undefined;
  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg || typeof svg.getAnimations !== 'function') return;
    const now = Number(document.timeline.currentTime ?? 0);
    if (stillNow) held.current ??= now - clock.current;
    else if (held.current !== null) {
      clock.current = now - held.current;
      held.current = null;
    }
    for (const a of svg.getAnimations({ subtree: true })) {
      if (!BREATH_ANIMATIONS.has((a as CSSAnimation).animationName)) continue;
      if (held.current === null) a.startTime = clock.current;
      else a.currentTime = held.current;
    }
  }, [breathing, life, core, beat, ghosts, stillNow]);

  // Порыв ветра раскручивает веер вокруг ядра — с того угла, где веер сейчас. Целый веер замирает на
  // угле, кратном 60°. Веер с позой лепестков (petals) — всегда целыми оборотами: поза может смениться
  // на ходу, и дыра или погашенная пластина должны встать на своё место.
  const spinRef = useRef<SVGGElement>(null);
  const spinAnim = useRef<Animation | null>(null);
  const gustStep = useRef(60);
  useLayoutEffect(() => {
    gustStep.current = ghosts ? 360 : 60;
  });
  useEffect(() => {
    const g = spinRef.current;
    if (!gust || !g || prefersReducedMotion()) return;
    const running = spinAnim.current?.playState === 'running';
    const now = getComputedStyle(g).transform;
    const m = now && now !== 'none' ? new DOMMatrix(now) : null;
    const from = m ? (Math.atan2(m.b, m.a) * 180) / Math.PI : 0;
    const step = gustStep.current;
    const to = Math.ceil((from + GUST_TURNS * 360) / step) * step;
    spinAnim.current?.cancel();
    spinAnim.current = g.animate(
      (running ? GUST_ON_THE_GO : GUST_FROM_REST).map(([offset, p]) => ({
        offset,
        transform: `rotate(${from + (to - from) * p}deg)`,
      })),
      { duration: GUST_MS },
    );
  }, [gust]);
  useEffect(() => () => spinAnim.current?.cancel(), []);

  /*
    Отклик ядра — только на новый cue после монтирования (R23: при первом показе тихо).
    - alarm — два удара на обёртке ядра и красное кольцо; повтор чаще раза в 1,5 с схлопывается —
      первая тревога доигрывает как есть; когда ядро ушло, звучит только кольцо;
    - burst — одно кольцо в тоне ядра; идущую тревогу не перебивает;
    - остальное — жест из языка Наблюдателя на группе ядра ([data-gesture] в motion.css).
    Кольцо в каждый момент одно. При уменьшенном движении ни ударов, ни колец, ни жестов.
  */
  const [mountCue] = useState(cue?.at);
  const fresh = cue && cue.at !== mountCue ? cue : null;
  const cueAt = fresh?.at;
  const cueGesture = fresh?.gesture;
  const cues = cue !== undefined;
  const alarmRef = useRef<SVGGElement>(null);
  const alarmAnim = useRef<Animation | null>(null);
  const lastAlarm = useRef(-Infinity);
  const gestureRef = useRef<SVGGElement>(null);
  const toneRingRef = useRef<SVGCircleElement>(null);
  const alarmRingRef = useRef<SVGCircleElement>(null);
  const ringAnim = useRef<Animation | null>(null);
  const ringAlarm = useRef(false);
  useLayoutEffect(() => {
    if (cueAt === undefined || cueGesture === undefined) return;
    const reduced = prefersReducedMotion();
    if (cueGesture === 'alarm' || cueGesture === 'burst') {
      const alarm = cueGesture === 'alarm';
      if (alarm) {
        const now = performance.now();
        if (now - lastAlarm.current < ALARM_GAP_MS) return;
        lastAlarm.current = now;
      } else if (ringAlarm.current && ringAnim.current?.playState === 'running') return;
      if (reduced) return;
      const ring = (alarm ? alarmRingRef : toneRingRef).current;
      if (ring) {
        ringAnim.current?.cancel();
        ringAnim.current = ring.animate(RING_BURST, { duration: RING_MS, easing: EASE_RING });
        ringAlarm.current = alarm;
      }
      const wrap = alarmRef.current;
      if (alarm && wrap) {
        alarmAnim.current?.cancel();
        alarmAnim.current = wrap.animate(ALARM_BEATS, { duration: ALARM_MS, easing: 'ease-out' });
      }
      return;
    }
    const g = gestureRef.current;
    if (!g || reduced) return;
    // Тот же жест подряд — заново: атрибут снимается и ставится после пересчёта стилей.
    g.removeAttribute('data-gesture');
    getComputedStyle(g).getPropertyValue('animation-name');
    g.setAttribute('data-gesture', cueGesture);
  }, [cueAt, cueGesture]);
  useEffect(
    () => () => {
      alarmAnim.current?.cancel();
      ringAnim.current?.cancel();
    },
    [],
  );

  /** Поза и темп лепестка k: куда встаёт и как туда идёт из прошлой позы. */
  const petalStyle = (k: number): CSSProperties => {
    const now = fan[k];
    if (!placed(now)) {
      // Сложенный лепесток стоит на шаг раньше (за соседом) и прозрачен. Первый — тоже: при раскрытии
      // он выходит поворотом, как остальные, а не проявляется на месте.
      return {
        transform: `rotate(${(k - 1) * 60}deg)`,
        opacity: 0,
        transitionDelay: `${reduced ? 0 : (5 - k) * FOLD_STEP_MS[tempo]}ms`,
      };
    }
    const pose = { transform: `rotate(${k * 60}deg)`, opacity: now === 'dim' ? DIM_OPACITY : 1 };
    if (placed(was[k]) && was[k] !== now) {
      // Пластина на месте, гаснет или загорается: меняется только прозрачность.
      return {
        ...pose,
        transitionDelay: '0ms',
        transitionDuration: 'var(--motion-slow), var(--motion-slow)',
        transitionTimingFunction: 'var(--ease-standard), var(--ease-standard)',
      };
    }
    if (reveal === 'bloom') {
      // По расписанию: каждый следующий позже и дольше, последние два тормозят сильнее.
      return {
        ...pose,
        transitionDelay: `${reduced ? 0 : schedule.delays[k]}ms`,
        transitionDuration: `${schedule.durations[k]}ms, ${Math.round(schedule.durations[k] * 0.7)}ms`,
        transitionTimingFunction: `${tempo === 'slow' && k >= 4 ? EASE_SETTLE_STRONG : EASE_SETTLE}, ease-out`,
      };
    }
    // Шаг загрузки или досадка: без расписания; открывшиеся за раз идут лесенкой по порядку.
    const t = reveal === 'snap' ? SNAP : STEP_REVEAL;
    const order = Math.max(0, arriving.indexOf(k));
    return {
      ...pose,
      transitionDelay: `${reduced ? 0 : order * t.stagger}ms`,
      transitionDuration: `${t.ms}ms, ${Math.round(t.ms * 0.7)}ms`,
      transitionTimingFunction: `${EASE_SETTLE}, ease-out`,
    };
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`${-MARK_HALF} ${-MARK_HALF} ${MARK_HALF * 2} ${MARK_HALF * 2}`}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-tone={tone === 'brand' ? undefined : tone}
      className={cn('block overflow-visible core-tone', rhythm === 'still' && 'mark-still', className)}
    >
      <defs>
        <radialGradient id={gradient} cx="32%" cy="27%" r="88%">
          <stop offset="0" className="stop-core-light" />
          <stop offset="0.45" className="stop-core" />
          <stop offset="1" className="stop-core-deep" />
        </radialGradient>
        <radialGradient
          id={toneGradient}
          cx="32%"
          cy="27%"
          r="88%"
          data-tone={paint === 'brand' ? undefined : paint}
          className="core-tone"
        >
          <stop offset="0" className="stop-tone-light" />
          <stop offset="0.45" className="stop-tone" />
          <stop offset="1" className="stop-tone-deep" />
        </radialGradient>
        <radialGradient id={glow}>
          <stop offset="0" className="stop-core" stopOpacity={0.45} />
          <stop offset="1" className="stop-core" stopOpacity={0} />
        </radialGradient>
        <clipPath id={clip}>
          {PETALS.map((k) => (
            <path key={k} d={PETAL_D} transform={`rotate(${k * 60})`} />
          ))}
        </clipPath>
      </defs>

      {/* Порядок обратный: следующий лепесток выходит из-за предыдущего, значит, рисуется под ним.
          Общие слои — раскрутка от порыва ветра и поворот на лепесток; ядро в них не участвует. */}
      <g ref={spinRef}>
        {/* Призраки пропущенных пластин — под лепестками. При раскрытии призрак приходит в такт своего
            лепестка: «пропущенный такт». */}
        {ghosts &&
          PETALS.map((k) => (
            <g key={k} transform={`rotate(${k * 60})`}>
              <g
                data-beat=""
                className={cn(breathes && 'animate-petal-breathe motion-reduce:animate-none')}
                style={breathes ? ({ '--k': k } as CSSProperties) : undefined}
              >
                <path
                  d={PETAL_D}
                  fill="none"
                  className={cn(
                    'stroke-petal transition-opacity duration-smooth ease-standard',
                    fan[k] === 'gap' ? 'opacity-60' : 'opacity-0',
                  )}
                  strokeWidth={1.25}
                  strokeDasharray="3 3"
                  vectorEffect="non-scaling-stroke"
                  style={{
                    transitionDelay: `${fan[k] === 'gap' && reveal === 'bloom' && !reduced && !placed(was[k]) && was[k] !== 'gap' ? schedule.delays[k] : 0}ms`,
                  }}
                />
              </g>
            </g>
          ))}
        <g data-beat="" className={cn(turns && 'animate-mark-turn motion-reduce:animate-none')}>
          {[...PETALS].reverse().map((k) => (
            <g
              key={k}
              data-folding={placed(fan[k]) ? undefined : true}
              data-tempo={tempo}
              className="petal-motion"
              style={petalStyle(k)}
            >
              {/* Дыхание: лепесток k прикрывается к первому на k долей — веер чуть сжимается и раскрывается. */}
              <g
                data-beat=""
                className={cn(breathes && 'animate-petal-breathe motion-reduce:animate-none')}
                style={breathes ? ({ '--k': k } as CSSProperties) : undefined}
              >
                <path d={PETAL_D} className={cn('fill-petal petal-depth', windowed && 'opacity-0')} />
                <path
                  d={PETAL_D}
                  className={cn('fill-rail stroke-petal petal-depth', !windowed && 'opacity-0')}
                  strokeWidth={1.5}
                  strokeOpacity={0.9}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            </g>
          ))}
        </g>
      </g>

      {/* Листопад за окнами: общий поток через весь знак, видно его только сквозь окна.
          Проявляется не сразу, а по мере того как темнеют окна. */}
      {leaves && windowed && (
        <g clipPath={`url(#${clip})`} className="animate-leaves-in">
          {LEAVES.map((l) => (
            <g
              key={l.key}
              className="leaf-fall motion-reduce:animate-none"
              style={
                {
                  '--sx': l.sx,
                  '--sy': l.sy,
                  '--ex': l.ex,
                  '--ey': l.ey,
                  '--o': l.opacity,
                  animationDuration: `${l.dur}s`,
                  animationDelay: `${l.delay}s`,
                } as CSSProperties
              }
            >
              <g transform={`rotate(${l.axis})`}>
                <g
                  className="leaf-sway motion-reduce:animate-none"
                  style={
                    {
                      '--sw': l.sway,
                      '--tilt': l.tilt,
                      animationDuration: `${l.swayDur}s`,
                    } as CSSProperties
                  }
                >
                  <circle r={l.scale * 26} fill={`url(#${glow})`} />
                  <path d={LEAF_D} transform={`scale(${l.scale})`} className={l.tone} fillOpacity={0.92} />
                </g>
              </g>
            </g>
          ))}
        </g>
      )}

      {/* Кольца отклика — между лепестками и ядром: обычное в тоне ядра, тревога красная. В покое
          прозрачны, играют только по отклику (RING_BURST). */}
      {cues && core !== 'none' && (
        <>
          <circle
            ref={toneRingRef}
            r={CORE_R + 8}
            fill="none"
            className="stroke-core-tone opacity-0 motion-reduce:hidden"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
          <circle
            ref={alarmRingRef}
            r={CORE_R + 8}
            fill="none"
            className="stroke-danger opacity-0 motion-reduce:hidden"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}

      {core === 'home' && (
        <g
          data-slot="observer"
          className="core-follow"
          style={coreShift ? { transform: `translate(${coreShift.x}px, ${coreShift.y}px)` } : undefined}
        >
          <g ref={alarmRef}>
            <g ref={gestureRef} className="origin-center transform-fill">
              <g data-beat="" className={cn(beat, beat && 'motion-reduce:animate-none')}>
                <circle r={CORE_R} fill={`url(#${gradient})`} />
                {/* Тон поверх коралла: проявляется с темпом срочности (core-tone). */}
                <circle r={CORE_R} fill={`url(#${toneGradient})`} data-tone-layer="" />
              </g>
            </g>
          </g>
        </g>
      )}
      {/* Кольцо в цвете тона: смысл тона читается и у ядра, которое ушло. */}
      {core === 'away' && (
        <circle
          data-core-ring=""
          r={CORE_R - 6}
          fill="none"
          className={tone === 'brand' ? 'stroke-petal' : 'stroke-tone-light'}
          strokeWidth={tone === 'danger' ? 2 : 1.25}
          strokeOpacity={tone === 'brand' ? 0.85 : 1}
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}
